import { ReactNode } from "react";
import { RelativeDateConfigModel } from "../annualWheel/RelativeDate.types.ts";
import { InputFieldTypeEnum } from "./inputFields/InputFields.types.ts";
import { exhaustiveGuard } from "../common/utilities.ts";
import { InformationSecurityFeatures, PlanType, PlatformFeatures, VendorManagementFeatures } from "../auth/planProvider/PlanProvider.types.ts";

export enum TemplateEntityType {
    Contract = 1,
    System = 2,
    LegalEntity = 3,
    IncidentLog = 4,
    Process = 5,
}

export enum ContractStandardField {
    Name = 1,
    Description = 2,
    ReferenceNumber = 3,
    Note = 4,
    ContractType = 5,
    Status = 6,

    StartDate = 7,
    EndDate = 8,
    SignatureDate = 9,
    NotificationOfContractExpiry = 10,

    Currency = 11,
    TotalAmount = 12,
    FinalisedAmount = 13,
    PaymentTerm = 14,

    BusinessAreas = 15,
    BusinessAreaAccess = 16,
    LegalEntities = 17,
    Tags = 18,

    ContractManager = 19,
    AdditionalUserAccess = 20,
    ExcludedUsers = 21,
    VisibleToAllUsers = 22,
    CounterParties = 23,
    SpecialTerms = 24,
    Documents = 25,
}

export enum SystemStandardField {
    SystemName = 1001,
    SystemDescription = 1002,
    SystemResponsible = 1003,
    DistributionArea = 1004,
    SystemEntityType = 1005,
    SystemStorageType = 1006,
    SystemDeletionPeriod = 1007,
    SystemNetwork = 1008,
    SystemIsCriticalForOperation = 1009,
    SystemUsesAI = 1010,
    SystemBusinessAreas = 1011,
    SystemSuppliers = 1012,
    SystemCertifications = 1013,
    SystemDataStorageLocations = 1014,
    SystemSupportAccessLocations = 1015,
    SystemSecurityMeasures = 1016,
    SystemPolicies = 1017,
    SystemDataTypes = 1018,
    SystemTags = 1019,
    SystemAssociatedAssets = 1020,
    SystemDataSubjects = 1021,
    SystemDataCategories = 1022,
    SystemAdditionalUserAccess = 1023,
    SystemExcludedUsers = 1024,
    SystemVisibleToAllUsers = 1025,
    SystemBusinessAreaAccess = 1026,
    SystemGroupCompanies = 1027,
    SystemStatus = 1028,
}

export enum LegalEntityStandardField {
    LegalEntityName = 2001,
    LegalEntityDescription = 2002,
    LegalEntityReferenceNumber = 2003,
    LegalEntityNote = 2004,
    LegalEntityAddress = 2005,
    LegalEntityZipCode = 2006,
    LegalEntityCity = 2007,
    LegalEntityCountry = 2008,
    LegalEntityCompanyRegistrationNumber = 2009,
    LegalEntityWebsite = 2010,
    LegalEntityTypes = 2011,
    LegalEntityApproved = 2012,
    LegalEntityStatus = 2013,
    LegalEntityBusinessAreas = 2014,
    LegalEntityTags = 2015,
    LegalEntityResponsible = 2016,
    // Covers the whole subcontractor block: the list and the "add subcontractors automatically" choice,
    // the way SystemSuppliers and SystemUsesAI each own several properties behind one field.
    LegalEntitySubContractors = 2017,
    LegalEntityAuditType = 2018,
    LegalEntityAuditFrequency = 2019,
    LegalEntityComplianceAreas = 2020,
    LegalEntityCertifications = 2021,
}

export enum IncidentLogStandardField {
    IncidentLogName = 3001,
    IncidentLogDescription = 3002,
    IncidentLogSeverity = 3003,
    IncidentLogTypes = 3004,
    IncidentLogStatus = 3005,
    IncidentLogTags = 3006,
    IncidentLogAdditionalUserAccess = 3007,
    IncidentLogExcludedUsers = 3008,
    IncidentLogVisibleToAllUsers = 3009,
    IncidentLogBusinessAreaAccess = 3010,
    IncidentLogGroupCompanies = 3011,
    // The incident log date fields each own the date and its time of day, since the two are always shown side by side.
    IncidentLogDiscoveryDate = 3012,
    // Also owns the "unknown" choice, which clears the start date and time.
    IncidentLogStartDate = 3014,
    IncidentLogEndDate = 3015,
    IncidentLogDataCategories = 3016,
    IncidentLogDataSubjects = 3017,
    IncidentLogEstimatedNumberOfAffectedPeople = 3018,
    IncidentLogRelations = 3019,
    IncidentLogConsequences = 3020,
    IncidentLogMeasures = 3021,
    IncidentLogReportedToAgency = 3022,
    IncidentLogAffectedPeopleNotified = 3025,
    IncidentLogDocuments = 3028,
    IncidentLogResponsible = 3029,
    IncidentLogNote = 3030,
    IncidentLogBusinessAreas = 3031,
    IncidentLogComplianceAreas = 3032,
    IncidentLogAccessRequestType = 3033,
    IncidentLogSuspectedUnlawfulOrMaliciousAct = 3034,
    IncidentLogAffectedServices = 3035,
    IncidentLogEstimatedNumberOfAffectedServiceRecipients = 3036,
    IncidentLogEstimatedFinancialLoss = 3037,
    // Owns both the answer and the affected countries, since the countries only make sense when the answer is yes.
    IncidentLogCrossBorderImpact = 3038,
    IncidentLogReceiptDate = 3039,
    // Owns the answer, the justification and the date the data subject was informed, since the latter two only make sense when the answer is yes.
    IncidentLogDeadlineExtended = 3042,
    IncidentLogDataSubjectName = 3043,
    // Owns both the answer and how the identity was verified, since the method only makes sense when the answer is yes.
    IncidentLogIdentityVerified = 3044,
    // Owns both the answer and the representative's name, since the name only makes sense when the answer is yes.
    IncidentLogRequestMadeThroughRepresentative = 3045,
    IncidentLogRequestedDataCategories = 3046,
    // Owns both the outcome and its justification, since the justification only makes sense when access is partial or refused.
    IncidentLogAccessRequestOutcome = 3047,
    // Owns the answer, the date and time and the method of the response, since the latter only make sense when the answer is yes.
    IncidentLogResponseSent = 3048,
    IncidentLogCopyOfPersonalDataProvided = 3049,
    // Only shown when the access request outcome is partial access or refused.
    IncidentLogInformedOfRightToComplain = 3050,
    IncidentLogSignificantIncidentAwarenessDate = 3051,
    IncidentLogReportedToAuthorities = 3052,
    IncidentLogServiceRecipientsNotified = 3053,
    IncidentLogRecoveryDate = 3054,
    IncidentLogAffectedSecurityProperties = 3055,
    IncidentLogPersonalDataInvolved = 3056,
    IncidentLogInformationSecurityReportedToAuthorities = 3057,
    IncidentLogAffectedPartiesNotified = 3058,
    IncidentLogSupplierNotificationDate = 3059,
    IncidentLogEstimatedNumberOfAffectedRecords = 3060,
}

export enum ProcessStandardField {
    ProcessName = 4001,
    ProcessDescription = 4002,
    ProcessStatus = 4003,
    ProcessBusinessAreas = 4004,
    ProcessComplianceAreas = 4005,
    ProcessTags = 4006,
    ProcessResponsible = 4007,
    ProcessAdditionalUserAccess = 4008,
    ProcessBusinessAreaAccess = 4009,
    ProcessExcludedUsers = 4010,
    ProcessVisibleToAllUsers = 4011,
    ProcessGroupCompanies = 4012,
    ProcessAssets = 4013,
    ProcessLegalEntities = 4014,
    ProcessDocuments = 4016,
    ProcessNote = 4017,
    ProcessProcessingActivities = 4018,
}

export type EntityTemplateStandardField =
    | ContractStandardField
    | SystemStandardField
    | LegalEntityStandardField
    | IncidentLogStandardField
    | ProcessStandardField;

export function isContractStandardField(field: EntityTemplateStandardField): field is ContractStandardField {
    return field < 1000;
}

export function isSystemStandardField(field: EntityTemplateStandardField): field is SystemStandardField {
    return field >= 1000 && field < 2000;
}

export function isLegalEntityStandardField(field: EntityTemplateStandardField): field is LegalEntityStandardField {
    return field >= 2000 && field < 3000;
}

export function isIncidentLogStandardField(field: EntityTemplateStandardField): field is IncidentLogStandardField {
    return field >= 3000 && field < 4000;
}

export function isProcessStandardField(field: EntityTemplateStandardField): field is ProcessStandardField {
    return field >= 4000 && field < 5000;
}

export interface EntityTemplateStep {
    id: string;
    name: string;
    fixed: boolean;
    order: number;
    fields: EntityTemplateField[];
}

export interface EntityTemplateField {
    id: string;
    standardFieldIdentifier?: EntityTemplateStandardField | null;
    customInputFieldId?: string;
    customInputFieldType?: InputFieldTypeEnum;
    customInputFieldName?: string;
    customInputFieldDescription?: string;
    order: number;
    required: boolean;
    removeDisabled: boolean;
    requiredAtCreation: boolean;
    requiredAtCreationDisabled: boolean;
    requiredDisabled: boolean;
    configurationData?: FieldConfigurationData | null;
    configuration?: ReactNode;
    // The value as the create request sends it for this field, so it can be put straight into the create form
    defaultValue?: unknown;
    defaultValueSupported?: boolean;
    disabled: boolean;
    subPermissions?: number[];
}

export interface SystemSupplierPermissions {
    hasDataProcessorAgreementPermission: boolean;
    hasStorageLocationPermission: boolean;
    hasSupportAccessPermission: boolean;
    hasSubProcessorPermission: boolean;
    hasTiaAndTransferBasis: boolean;
}

enum SystemDataProcessorSubPermission {
    DataProcessorAgreement = 60,
    StorageLocation = 61,
    SupportAccess = 62,
    SubProcessors = 63,
    TiaAndTransferBasis = 65,
}

export const allSupplierPermissionsGranted: SystemSupplierPermissions = {
    hasDataProcessorAgreementPermission: true,
    hasStorageLocationPermission: true,
    hasSupportAccessPermission: true,
    hasSubProcessorPermission: true,
    hasTiaAndTransferBasis: true,
};

export function resolveSupplierPermissions(subPermissions?: number[]): SystemSupplierPermissions {
    if (!subPermissions) return allSupplierPermissionsGranted;

    const has = (p: SystemDataProcessorSubPermission) => subPermissions.includes(p);
    return {
        hasDataProcessorAgreementPermission: has(SystemDataProcessorSubPermission.DataProcessorAgreement),
        hasStorageLocationPermission: has(SystemDataProcessorSubPermission.StorageLocation),
        hasSupportAccessPermission: has(SystemDataProcessorSubPermission.SupportAccess),
        hasSubProcessorPermission: has(SystemDataProcessorSubPermission.SubProcessors),
        hasTiaAndTransferBasis: has(SystemDataProcessorSubPermission.TiaAndTransferBasis),
    };
}

export interface FieldConfigurationData {
    type: number;
    value: string;
}

export enum FieldConfigurationValueType {
    Boolean = 1,
    String = 2,
    Int = 3,
    Json = 4,
}

export enum FormulaTokenKind {
    Field = 1,
    Operator = 2,
    Bracket = 3,
}

export enum FormulaOperator {
    Add = 1,
    Subtract = 2,
    Multiply = 3,
    Divide = 4,
}

export interface FormulaToken {
    kind: FormulaTokenKind;
    fieldId?: string;
    // Input field types are immutable after creation, so the token carries the type and evaluation needs no lookup
    fieldType?: InputFieldTypeEnum;
    operator?: FormulaOperator;
    open?: boolean;
}

export enum PeriodUnit {
    Years = 1,
    Months = 2,
}

export interface DateFieldReference {
    standardField?: EntityTemplateStandardField;
    customFieldId?: string;
}

export interface DeletionResult {
    isDeleted: boolean;
    name: string;
}

export interface DatePeriodConfiguration {
    from: DateFieldReference;
    to: DateFieldReference;
    unit: PeriodUnit;
}

export interface EntityTemplate {
    id: string;
    name: string;
    description?: string;
    isStandardTemplate: boolean;
    steps: Array<EntityTemplateStep>;
    templateType: TemplateEntityType;
}

export interface EntityTemplateRowViewModel {
    id: string;
    name: string;
    description?: string;
    isStandardTemplate: boolean;
    active: boolean;
    hasAccess: boolean;
    unlockingPlans: Array<PlanType>;
}

export interface EntityTemplateRowTableModel extends EntityTemplateRowViewModel {
    activeStatusString: string;
    lockedTooltip?: string;
}

export interface EntityTemplateTableViewModel extends EntityTemplateRowViewModel {
    isStandardTemplateString?: string;
}

export interface SelectableInputField {
    id: string;
    name: string;
    description?: string;
    type: InputFieldTypeEnum;
    configuration?: {
        currencyCode?: string;
        list?: Array<{ id: string; name: string }>;
    };
}

export interface RequiredAtCreationField {
    entityTemplateFieldId: string;
    standardFieldIdentifier?: EntityTemplateStandardField | null;
    customInputFieldId?: string;
    customInputFieldType?: InputFieldTypeEnum;
    customInputFieldName?: string;
    customInputFieldDescription?: string;
    stepOrder: number;
    fieldOrder: number;
    defaultValue?: unknown;
}

export interface SelectableStandardEntityTemplate {
    id: string;
    name: string;
    hasAccess: boolean;
    unlockingPlans: Array<PlanType>;
}

export interface SelectableEntityTemplate {
    id: string;
    name: string;
    hasAccess: boolean;
    unlockingPlans: Array<PlanType>;
    missingRequiredAtCreationFieldAccess: boolean;
    obligationTemplates: Array<SelectableObligationTemplates>;
    requiredAtCreationFields: Array<RequiredAtCreationField>;
}

export interface SelectableObligationTemplates {
    id: string;
    name: string;
    startDate?: RelativeDateConfigModel;
    endDate?: RelativeDateConfigModel;
    containsRelativeDate: boolean;
    isCarriedOutOnce: boolean;
    isMandatory: boolean;
    isUsed: boolean;
}

export function getStandardFieldTranslationKey(field: EntityTemplateStandardField): string {
    switch (field) {
        case ContractStandardField.Name:
            return "contract";
        case ContractStandardField.Description:
            return "description";
        case ContractStandardField.ReferenceNumber:
            return "referenceNumber";
        case ContractStandardField.Note:
            return "note";
        case ContractStandardField.ContractType:
            return "contractType";
        case ContractStandardField.Status:
            return "status";
        case ContractStandardField.StartDate:
            return "startDate";
        case ContractStandardField.EndDate:
            return "endDate";
        case ContractStandardField.SignatureDate:
            return "signatureDate";
        case ContractStandardField.NotificationOfContractExpiry:
            return "notificationOfContractExpiry";
        case ContractStandardField.Currency:
            return "currency";
        case ContractStandardField.TotalAmount:
            return "totalAmount";
        case ContractStandardField.FinalisedAmount:
            return "finalisedAmount";
        case ContractStandardField.PaymentTerm:
            return "paymentTerms";
        case ContractStandardField.BusinessAreas:
            return "businessAreas";
        case ContractStandardField.BusinessAreaAccess:
            return "businessAreaAccess";
        case ContractStandardField.LegalEntities:
            return "allGroupEntityAccess";
        case ContractStandardField.Tags:
            return "tags";
        case ContractStandardField.ContractManager:
            return "contractManager";
        case ContractStandardField.AdditionalUserAccess:
            return "additionalUserAccess";
        case ContractStandardField.ExcludedUsers:
            return "excludedUsers";
        case ContractStandardField.VisibleToAllUsers:
            return "visibleToAllUsers";
        case ContractStandardField.CounterParties:
            return "contractParties";
        case ContractStandardField.SpecialTerms:
            return "specialTerms";
        case ContractStandardField.Documents:
            return "documents";
        case SystemStandardField.SystemName:
            return "asset";
        case SystemStandardField.SystemDescription:
            return "description";
        case SystemStandardField.SystemResponsible:
            return "responsible";
        case SystemStandardField.DistributionArea:
            return "distributionArea";
        case SystemStandardField.SystemEntityType:
            return "entityType";
        case SystemStandardField.SystemStorageType:
            return "storageType";
        case SystemStandardField.SystemDeletionPeriod:
            return "deletionPeriod";
        case SystemStandardField.SystemNetwork:
            return "network";
        case SystemStandardField.SystemIsCriticalForOperation:
            return "operationCriticalSystem";
        case SystemStandardField.SystemUsesAI:
            return "aiHeader";
        case SystemStandardField.SystemBusinessAreas:
            return "businessAreas";
        case SystemStandardField.SystemSuppliers:
            return "supplier";
        case SystemStandardField.SystemCertifications:
            return "certification";
        case SystemStandardField.SystemDataStorageLocations:
            return "storageLocation";
        case SystemStandardField.SystemSupportAccessLocations:
            return "supportAccess";
        case SystemStandardField.SystemSecurityMeasures:
            return "securityMeasures";
        case SystemStandardField.SystemPolicies:
            return "systemPolicyHeader";
        case SystemStandardField.SystemDataTypes:
            return "dataType";
        case SystemStandardField.SystemTags:
            return "tags";
        case SystemStandardField.SystemAssociatedAssets:
            return "associatedAssets";
        case SystemStandardField.SystemDataSubjects:
            return "chooseDataSubjectsForSystem";
        case SystemStandardField.SystemDataCategories:
            return "dataCategoriesOnAssetsHeader";
        case SystemStandardField.SystemAdditionalUserAccess:
            return "additionalUserAccess";
        case SystemStandardField.SystemBusinessAreaAccess:
            return "businessAreaAccess";
        case SystemStandardField.SystemExcludedUsers:
            return "excludedUsers";
        case SystemStandardField.SystemGroupCompanies:
            return "allGroupEntityAccess";
        case SystemStandardField.SystemVisibleToAllUsers:
            return "visibleToAllUsers";
        case SystemStandardField.SystemStatus:
            return "systemStatus";
        case LegalEntityStandardField.LegalEntityName:
            return "legalEntity";
        case LegalEntityStandardField.LegalEntityDescription:
            return "description";
        case LegalEntityStandardField.LegalEntityReferenceNumber:
            return "referenceNumber";
        case LegalEntityStandardField.LegalEntityNote:
            return "note";
        case LegalEntityStandardField.LegalEntityAddress:
            return "address";
        case LegalEntityStandardField.LegalEntityZipCode:
            return "zipCode";
        case LegalEntityStandardField.LegalEntityCity:
            return "city";
        case LegalEntityStandardField.LegalEntityCountry:
            return "country";
        case LegalEntityStandardField.LegalEntityCompanyRegistrationNumber:
            return "companyRegistrationNumber";
        case LegalEntityStandardField.LegalEntityWebsite:
            return "website";
        case LegalEntityStandardField.LegalEntityTypes:
            return "type";
        case LegalEntityStandardField.LegalEntityApproved:
            return "legalEntityApproved";
        case LegalEntityStandardField.LegalEntityStatus:
            return "status";
        case LegalEntityStandardField.LegalEntityBusinessAreas:
            return "businessAreas";
        case LegalEntityStandardField.LegalEntityTags:
            return "tags";
        case LegalEntityStandardField.LegalEntityResponsible:
            return "responsible";
        case LegalEntityStandardField.LegalEntitySubContractors:
            return "subContractors";
        case LegalEntityStandardField.LegalEntityAuditType:
            return "legalEntityAuditType";
        case LegalEntityStandardField.LegalEntityAuditFrequency:
            return "legalEntityFrequency";
        case LegalEntityStandardField.LegalEntityComplianceAreas:
            return "areas";
        case LegalEntityStandardField.LegalEntityCertifications:
            return "certifications";
        case IncidentLogStandardField.IncidentLogName:
            return "name";
        case IncidentLogStandardField.IncidentLogDescription:
            return "description";
        case IncidentLogStandardField.IncidentLogSeverity:
            return "severity";
        case IncidentLogStandardField.IncidentLogTypes:
            return "type";
        case IncidentLogStandardField.IncidentLogStatus:
            return "status";
        case IncidentLogStandardField.IncidentLogTags:
            return "tags";
        case IncidentLogStandardField.IncidentLogResponsible:
            return "responsible";
        case IncidentLogStandardField.IncidentLogNote:
            return "note";
        case IncidentLogStandardField.IncidentLogAdditionalUserAccess:
            return "additionalUserAccess";
        case IncidentLogStandardField.IncidentLogExcludedUsers:
            return "excludedUsers";
        case IncidentLogStandardField.IncidentLogVisibleToAllUsers:
            return "visibleToAllUsers";
        case IncidentLogStandardField.IncidentLogBusinessAreaAccess:
            return "businessAreaAccess";
        case IncidentLogStandardField.IncidentLogGroupCompanies:
            return "allGroupEntityAccess";
        case IncidentLogStandardField.IncidentLogDiscoveryDate:
            return "discoveryDate";
        case IncidentLogStandardField.IncidentLogReceiptDate:
            return "receiptDate";
        case IncidentLogStandardField.IncidentLogDeadlineExtended:
            return "deadlineExtended";
        case IncidentLogStandardField.IncidentLogDataSubjectName:
            return "dataSubjectName";
        case IncidentLogStandardField.IncidentLogIdentityVerified:
            return "identityVerified";
        case IncidentLogStandardField.IncidentLogRequestMadeThroughRepresentative:
            return "requestMadeThroughRepresentative";
        case IncidentLogStandardField.IncidentLogRequestedDataCategories:
            return "requestedDataCategories";
        case IncidentLogStandardField.IncidentLogAccessRequestOutcome:
            return "accessRequestOutcome";
        case IncidentLogStandardField.IncidentLogResponseSent:
            return "responseSent";
        case IncidentLogStandardField.IncidentLogCopyOfPersonalDataProvided:
            return "copyOfPersonalDataProvided";
        case IncidentLogStandardField.IncidentLogInformedOfRightToComplain:
            return "informedOfRightToComplain";
        case IncidentLogStandardField.IncidentLogStartDate:
            return "incidentTime";
        case IncidentLogStandardField.IncidentLogEndDate:
            return "incidentEndTime";
        case IncidentLogStandardField.IncidentLogDataCategories:
            return "dataCategories";
        case IncidentLogStandardField.IncidentLogDataSubjects:
            return "categoryOfDataSubject";
        case IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedPeople:
            return "estimatedNumberOfAffectedPeople";
        case IncidentLogStandardField.IncidentLogRelations:
            return "relations";
        case IncidentLogStandardField.IncidentLogReportedToAgency:
            return "reportToAuthorities";
        case IncidentLogStandardField.IncidentLogAffectedPeopleNotified:
            return "notificationOfDataSubjects";
        case IncidentLogStandardField.IncidentLogDocuments:
            return "documents";
        case IncidentLogStandardField.IncidentLogConsequences:
            return "consequences";
        case IncidentLogStandardField.IncidentLogMeasures:
            return "measures";
        case IncidentLogStandardField.IncidentLogBusinessAreas:
            return "businessAreas";
        case IncidentLogStandardField.IncidentLogComplianceAreas:
            return "areas";
        case IncidentLogStandardField.IncidentLogAccessRequestType:
            return "accessRequestType";
        case IncidentLogStandardField.IncidentLogSuspectedUnlawfulOrMaliciousAct:
            return "suspectedUnlawfulOrMaliciousAct";
        case IncidentLogStandardField.IncidentLogAffectedServices:
            return "affectedServices";
        case IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedServiceRecipients:
            return "estimatedNumberOfAffectedServiceRecipients";
        case IncidentLogStandardField.IncidentLogEstimatedFinancialLoss:
            return "incidentLogEstimatedFinancialLoss";
        case IncidentLogStandardField.IncidentLogCrossBorderImpact:
            return "crossBorderImpact";
        case IncidentLogStandardField.IncidentLogSignificantIncidentAwarenessDate:
            return "significantIncidentAwarenessDate";
        case IncidentLogStandardField.IncidentLogReportedToAuthorities:
            return "nis2ReportingToAuthorities";
        case IncidentLogStandardField.IncidentLogServiceRecipientsNotified:
            return "notificationOfServiceRecipients";
        case IncidentLogStandardField.IncidentLogRecoveryDate:
            return "recoveryDate";
        case IncidentLogStandardField.IncidentLogAffectedSecurityProperties:
            return "affectedSecurityProperties";
        case IncidentLogStandardField.IncidentLogPersonalDataInvolved:
            return "personalDataInvolved";
        case IncidentLogStandardField.IncidentLogInformationSecurityReportedToAuthorities:
            return "informationSecurityReportedToAuthorities";
        case IncidentLogStandardField.IncidentLogAffectedPartiesNotified:
            return "notificationOfAffectedParties";
        case IncidentLogStandardField.IncidentLogSupplierNotificationDate:
            return "supplierNotificationDate";
        case IncidentLogStandardField.IncidentLogEstimatedNumberOfAffectedRecords:
            return "estimatedNumberOfAffectedRecords";
        case ProcessStandardField.ProcessName:
            return "name";
        case ProcessStandardField.ProcessDescription:
            return "description";
        case ProcessStandardField.ProcessStatus:
            return "status";
        case ProcessStandardField.ProcessBusinessAreas:
            return "businessAreas";
        case ProcessStandardField.ProcessComplianceAreas:
            return "areas";
        case ProcessStandardField.ProcessTags:
            return "tags";
        case ProcessStandardField.ProcessResponsible:
            return "responsible";
        case ProcessStandardField.ProcessAdditionalUserAccess:
            return "additionalUserAccess";
        case ProcessStandardField.ProcessBusinessAreaAccess:
            return "businessAreaAccess";
        case ProcessStandardField.ProcessExcludedUsers:
            return "excludedUsers";
        case ProcessStandardField.ProcessVisibleToAllUsers:
            return "visibleToAllUsers";
        case ProcessStandardField.ProcessGroupCompanies:
            return "allGroupEntityAccess";
        case ProcessStandardField.ProcessAssets:
            return "assets";
        case ProcessStandardField.ProcessLegalEntities:
            return "legalEntities";
        case ProcessStandardField.ProcessDocuments:
            return "documents";
        case ProcessStandardField.ProcessNote:
            return "note";
        case ProcessStandardField.ProcessProcessingActivities:
            return "processingActivities";
        default:
            return exhaustiveGuard(field);
    }
}

export interface EntityTemplateTranslationKeys {
    template: string;
    templates: string;
    standardTemplate: string;
    createTemplate: string;
    editTemplate: string;
    deactivateInfo: string;
    noTemplateMatchesQuery: string;
    noTemplateText: string;
    inputFields: string;
}

export function getEntityTemplateTranslationKeys(type: TemplateEntityType): EntityTemplateTranslationKeys {
    switch (type) {
        case TemplateEntityType.Contract:
            return {
                template: "contractTemplate",
                templates: "contractTemplates",
                standardTemplate: "standardContractTemplate",
                createTemplate: "createContractTemplate",
                editTemplate: "editContractTemplate",
                deactivateInfo: "deActiveContractTemplateInfo",
                noTemplateMatchesQuery: "noContractTemplateMatchesQuery",
                noTemplateText: "noContractTemplateText",
                inputFields: "contractInputFields",
            };
        case TemplateEntityType.System:
            return {
                template: "systemTemplate",
                templates: "systemTemplates",
                standardTemplate: "standardSystemTemplate",
                createTemplate: "createSystemTemplate",
                editTemplate: "editSystemTemplate",
                deactivateInfo: "deActiveSystemTemplateInfo",
                noTemplateMatchesQuery: "noSystemTemplateMatchesQuery",
                noTemplateText: "noSystemTemplateText",
                inputFields: "systemInputFields",
            };
        case TemplateEntityType.LegalEntity:
            return {
                template: "legalEntityTemplate",
                templates: "legalEntityTemplates",
                standardTemplate: "standardLegalEntityTemplate",
                createTemplate: "createLegalEntityTemplate",
                editTemplate: "editLegalEntityTemplate",
                deactivateInfo: "deActiveLegalEntityTemplateInfo",
                noTemplateMatchesQuery: "noLegalEntityTemplateMatchesQuery",
                noTemplateText: "noLegalEntityTemplateText",
                inputFields: "legalEntityInputFields",
            };
        case TemplateEntityType.IncidentLog:
            return {
                template: "incidentLogTemplate",
                templates: "incidentLogTemplates",
                standardTemplate: "standardIncidentLogTemplate",
                createTemplate: "createIncidentLogTemplate",
                editTemplate: "editIncidentLogTemplate",
                deactivateInfo: "deActiveIncidentLogTemplateInfo",
                noTemplateMatchesQuery: "noIncidentLogTemplateMatchesQuery",
                noTemplateText: "noIncidentLogTemplateText",
                inputFields: "incidentLogInputFields",
            };
        case TemplateEntityType.Process:
            return {
                template: "processTemplate",
                templates: "processTemplates",
                standardTemplate: "standardProcessTemplate",
                createTemplate: "createProcessTemplate",
                editTemplate: "editProcessTemplate",
                deactivateInfo: "deActiveProcessTemplateInfo",
                noTemplateMatchesQuery: "noProcessTemplateMatchesQuery",
                noTemplateText: "noProcessTemplateText",
                inputFields: "processInputFields",
            };
        default:
            return exhaustiveGuard(type);
    }
}

export function getStandardFieldsByEntityType(type: TemplateEntityType): EntityTemplateStandardField[] {
    const sourceEnum = getStandardFieldEnum(type);

    return Object.values(sourceEnum).filter((value): value is EntityTemplateStandardField => typeof value === "number");
}

function getStandardFieldEnum(type: TemplateEntityType) {
    switch (type) {
        case TemplateEntityType.Contract:
            return ContractStandardField;
        case TemplateEntityType.System:
            return SystemStandardField;
        case TemplateEntityType.LegalEntity:
            return LegalEntityStandardField;
        case TemplateEntityType.IncidentLog:
            return IncidentLogStandardField;
        case TemplateEntityType.Process:
            return ProcessStandardField;
        default:
            return exhaustiveGuard(type);
    }
}

export function hasSystemFieldFeatureAccess(
    field: SystemStandardField,
    informationSecurityFeatures: InformationSecurityFeatures,
    platformFeatures: PlatformFeatures
) {
    switch (field) {
        case SystemStandardField.SystemTags:
            return platformFeatures.tags;
        case SystemStandardField.SystemStorageType:
            return informationSecurityFeatures.storageType;
        case SystemStandardField.SystemEntityType:
            return informationSecurityFeatures.entityType;
        case SystemStandardField.SystemDataTypes:
            return informationSecurityFeatures.dataType;
        case SystemStandardField.SystemCertifications:
            return informationSecurityFeatures.certifications;
        case SystemStandardField.SystemUsesAI:
            return informationSecurityFeatures.ai;
        case SystemStandardField.SystemAssociatedAssets:
            return informationSecurityFeatures.associatedAssets;
        case SystemStandardField.SystemDataSubjects:
        case SystemStandardField.SystemDataCategories:
            return informationSecurityFeatures.personalData;
        default:
            return true;
    }
}

export function getFixedLegalEntityStandardFields(): Array<LegalEntityStandardField> {
    return [LegalEntityStandardField.LegalEntityName, LegalEntityStandardField.LegalEntityCountry];
}

export function getFixedIncidentLogStandardFields(): Array<IncidentLogStandardField> {
    return [IncidentLogStandardField.IncidentLogName];
}

export function getFixedProcessStandardFields(): Array<ProcessStandardField> {
    return [ProcessStandardField.ProcessName];
}

export function hasLegalEntityFieldFeatureAccess(
    field: LegalEntityStandardField,
    informationSecurityFeatures: InformationSecurityFeatures,
    vendorManagementFeatures: VendorManagementFeatures,
    platformFeatures: PlatformFeatures
) {
    switch (field) {
        case LegalEntityStandardField.LegalEntityTags:
            return platformFeatures.tags;
        case LegalEntityStandardField.LegalEntityAuditType:
        case LegalEntityStandardField.LegalEntityAuditFrequency:
        case LegalEntityStandardField.LegalEntityComplianceAreas:
            return vendorManagementFeatures.hasVendorManagement;
        // Certifications sits on the vendor management gated compliance step, so it stays hidden without vendor
        // management even when the information security plan would otherwise grant it.
        case LegalEntityStandardField.LegalEntityCertifications:
            return vendorManagementFeatures.hasVendorManagement && (informationSecurityFeatures.certifications || vendorManagementFeatures.certifications);
        default:
            return true;
    }
}

export function hasIncidentLogFieldFeatureAccess(
    field: IncidentLogStandardField,
    vendorManagementFeatures: VendorManagementFeatures,
    platformFeatures: PlatformFeatures
) {
    switch (field) {
        case IncidentLogStandardField.IncidentLogTags:
            return platformFeatures.tags;
        case IncidentLogStandardField.IncidentLogComplianceAreas:
            return vendorManagementFeatures.hasVendorManagement;
        default:
            return true;
    }
}

export function hasProcessFieldFeatureAccess(
    field: ProcessStandardField,
    vendorManagementFeatures: VendorManagementFeatures,
    platformFeatures: PlatformFeatures
) {
    switch (field) {
        case ProcessStandardField.ProcessTags:
            return platformFeatures.tags;
        case ProcessStandardField.ProcessComplianceAreas:
            return vendorManagementFeatures.hasVendorManagement;
        default:
            return true;
    }
}

export function hasStandardFieldFeatureAccess(
    field: EntityTemplateStandardField,
    informationSecurityFeatures: InformationSecurityFeatures,
    vendorManagementFeatures: VendorManagementFeatures,
    platformFeatures: PlatformFeatures
) {
    if (isSystemStandardField(field)) {
        return hasSystemFieldFeatureAccess(field, informationSecurityFeatures, platformFeatures);
    }

    if (isLegalEntityStandardField(field)) {
        return hasLegalEntityFieldFeatureAccess(field, informationSecurityFeatures, vendorManagementFeatures, platformFeatures);
    }

    if (isIncidentLogStandardField(field)) {
        return hasIncidentLogFieldFeatureAccess(field, vendorManagementFeatures, platformFeatures);
    }

    if (isProcessStandardField(field)) {
        return hasProcessFieldFeatureAccess(field, vendorManagementFeatures, platformFeatures);
    }

    return true;
}
